import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import { HiveActivity } from 'app/model/pipeline/hive.activity';

import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { HDInsightLinkedService } from 'app/model/linkedservice/hdinsight.linkedservice';
import { ReferenceAdapter } from '../reference.adapter';
import { toArgsArray, toArgsString } from 'app/model/adapters/activities/arguments-adapter.util';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { ValueAdapter } from './../value.adapter';
import { FilePath } from 'app/model/dataset/file-path';
import _ from 'lodash';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(HiveActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            scriptPath: '*',
            getDebugInfo: '*',
            arguments: '*',
            storageLinkedServices: linkedServiceReferenceWithoutParameterizationRemainer,
            scriptLinkedService: linkedServiceReferenceWithoutParameterizationRemainer,
            defines: '*',
            variables: '*',
            queryTimeout: '*'
        }
    }
})
export class HDInsightHiveActivityAdapter extends ActivityAsyncAdapter<HiveActivity, Model.HDInsightHiveActivity> {

    public model(): IModel {
        return Metamodel.modelFor(HiveActivity);
    }

    protected async _toResource(activity: HiveActivity): Promise<Model.HDInsightHiveActivity> {
        const modelHDInsightHiveActivity: Model.HDInsightHiveActivity = super._getCommonActivityProperties(activity, 'HDInsightHive') as Model.HDInsightHiveActivity;
        const hiveActivity: HiveActivity = activity as HiveActivity;

        const scriptPath = ValueAdapter.toResource(hiveActivity, HiveActivity.SCRIPT_PATH);

        modelHDInsightHiveActivity.typeProperties = {
            scriptPath: scriptPath.type ? scriptPath : scriptPath.path,
            arguments: toArgsArray(hiveActivity.hiveActivityArguments, hiveActivity, HiveActivity.ARGUMENTS),
            getDebugInfo: hiveActivity.getDebugInfo,
            defines: toParameterObject(hiveActivity.defines),
            variables: toParameterObject(hiveActivity.variables),
            queryTimeout: hiveActivity.queryTimeout
        };

        if (hiveActivity.scriptLinkedService) {
            modelHDInsightHiveActivity.typeProperties.scriptLinkedService = this._toLinkedServiceReferenceModel(hiveActivity.scriptLinkedService);
        }

        if (hiveActivity.linkedService) {
            modelHDInsightHiveActivity.linkedServiceName = this._toLinkedServiceReferenceModel(hiveActivity.linkedService);
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on hive activity
            _.merge(modelHDInsightHiveActivity, parsed);
        }

        return modelHDInsightHiveActivity;
    }

    protected async _toModel(modelActivity: Model.HDInsightHiveActivity): Promise<HiveActivity> {
        const activity: HiveActivity = HiveActivity.MODEL.create<HiveActivity>(modelActivity.name);
        const modelHiveActivity = modelActivity as Model.HDInsightHiveActivity;

        this._updatedCommonProperties(modelActivity, activity);

        if (modelHiveActivity.typeProperties) {
            if (modelHiveActivity.typeProperties.scriptPath && modelHiveActivity.typeProperties.scriptPath.type) {
                ValueAdapter.toModel(activity, HiveActivity.SCRIPT_PATH, modelHiveActivity.typeProperties.scriptPath);
            }  else  {
                activity.scriptPath = new  FilePath();
                activity.scriptPath.path = modelHiveActivity.typeProperties.scriptPath || '';
            }

            const args = toArgsString(activity, modelHiveActivity.typeProperties.arguments, HiveActivity.ARGUMENTS);
            if (args) {
                activity.hiveActivityArguments = args;
            }
            activity.getDebugInfo = modelHiveActivity.typeProperties.getDebugInfo;
            activity.defines = toParameterClass(modelHiveActivity.typeProperties.defines, activity);
            activity.variables = toParameterClass(modelHiveActivity.typeProperties.variables, activity);
            activity.queryTimeout = modelHiveActivity.typeProperties.queryTimeout;
            if (modelHiveActivity.typeProperties.scriptLinkedService !== undefined) {
                activity.scriptLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(Metamodel.modelFor(AzureStorageLinkedService),
                    modelHiveActivity.typeProperties.scriptLinkedService, this.context);
            }
        }

        if (modelHiveActivity.linkedServiceName !== undefined) {
            activity.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(Metamodel.modelFor(HDInsightLinkedService),
                modelHiveActivity.linkedServiceName, this.context);
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelHiveActivity, HiveActivity.MODEL));

        return activity;
    }
}
